/**
 * DeliveryReschedulingPage — citizen self-service delivery date rescheduling.
 *
 * Sibling of F-076 (address-change). Lets a citizen shift the delivery date
 * for an in-flight parcel without changing the address. Calls
 *
 *   POST /v1/mail-items/:trackingNumber/reschedule-requests
 *   GET  /v1/mail-items/:trackingNumber/reschedule-requests
 *
 * on service-mail. The first reschedule per parcel is free; subsequent ones
 * cost ₦200, debited automatically against the citizen's NIPOST Pay wallet
 * by the backend.
 *
 * Flow:
 *   Step 1: enter tracking number → look up parcel + existing reschedules.
 *   Step 2: pick a future delivery date.
 *   Step 3: confirm — preview fee + wallet balance + submit.
 *   Success: confirmation card + a "Recent reschedules" history list.
 *
 * Error handling:
 *   - 404 — tracking number not found, or caller is not the parcel owner.
 *   - 409 — parcel is already DELIVERED / in a terminal status.
 *   - 402 — wallet balance insufficient for the ₦200 fee (only on 2nd+).
 *   - 400 — server-side validation error (malformed date, past date, etc.).
 *   - Other — generic network / server error.
 */
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock,
  Loader2,
  Search,
  Wallet,
  XCircle,
} from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import {
  listRescheduleRequests,
  requestReschedule,
  type RescheduleResponse,
} from '../../lib/api/reschedule';
import { getOwnWallet, type WalletResponse } from '../../lib/api/wallet';
import { PageHeader } from './PortalUI';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green';

interface DayCell {
  readonly iso: string;
  readonly label: string;
  readonly day: number;
  readonly month: string;
  readonly disabled: boolean;
}

/** 14 future calendar days starting from tomorrow. */
function futureDays(count = 14): readonly DayCell[] {
  const cells: DayCell[] = [];
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  for (let i = 1; i <= count; i += 1) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    cells.push({
      iso: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString('en-NG', { weekday: 'short' }),
      day: d.getDate(),
      month: d.toLocaleDateString('en-NG', { month: 'short' }),
      disabled: false,
    });
  }
  return cells;
}

function formatNgn(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return '—';
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type LookupState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loaded'; existing: readonly RescheduleResponse[]; tracking: string }
  | { kind: 'error'; message: string };

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; result: RescheduleResponse }
  | { kind: 'error'; message: string };

export function DeliveryReschedulingPage(): JSX.Element {
  const [tracking, setTracking] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [lookup, setLookup] = useState<LookupState>({ kind: 'idle' });
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });
  const [wallet, setWallet] = useState<WalletResponse | null>(null);

  const days = futureDays();

  // Best-effort wallet balance fetch (so the citizen sees what's available).
  useEffect(() => {
    let cancelled = false;
    void getOwnWallet()
      .then((w) => { if (!cancelled) setWallet(w); })
      .catch(() => { /* silently ignore — wallet may not exist yet */ });
    return () => { cancelled = true; };
  }, []);

  /** Pre-computed fee for the next reschedule on the looked-up parcel. */
  const nextFeeNgn: string =
    lookup.kind === 'loaded' && lookup.existing.length > 0 ? '200.00' : '0.00';
  const isFirstReschedule = lookup.kind === 'loaded' && lookup.existing.length === 0;

  const canConfirm =
    lookup.kind === 'loaded' &&
    selectedDate.length > 0 &&
    submit.kind !== 'submitting';

  async function handleLookup(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const tn = tracking.trim().toUpperCase();
    if (tn.length === 0) return;
    setLookup({ kind: 'loading' });
    setSubmit({ kind: 'idle' });
    setSelectedDate('');
    try {
      const existing = await listRescheduleRequests(tn);
      setLookup({ kind: 'loaded', existing, tracking: tn });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setLookup({
            kind: 'error',
            message: 'Tracking number not found, or you are not the registered owner.',
          });
        } else if (err.status === 401 || err.status === 403) {
          setLookup({
            kind: 'error',
            message: 'You do not have permission to view this parcel. Please sign in again.',
          });
        } else {
          setLookup({ kind: 'error', message: `Lookup failed (${err.status}).` });
        }
      } else {
        setLookup({ kind: 'error', message: 'Network error during lookup.' });
      }
    }
  }

  async function handleConfirm(): Promise<void> {
    if (lookup.kind !== 'loaded' || selectedDate.length === 0) return;
    setSubmit({ kind: 'submitting' });
    try {
      const result = await requestReschedule(lookup.tracking, {
        requestedDeliveryDate: selectedDate,
        ...(reason.trim().length > 0 && { reason: reason.trim() }),
      });
      setSubmit({ kind: 'success', result });
      // Refresh the list so the new entry shows up underneath.
      const refreshed = await listRescheduleRequests(lookup.tracking).catch(() => lookup.existing);
      setLookup({ kind: 'loaded', existing: refreshed, tracking: lookup.tracking });
      // Refresh wallet balance after a fee was charged.
      if (result.feeMinor > 0) {
        void getOwnWallet().then(setWallet).catch(() => { /* ignore */ });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setSubmit({ kind: 'error', message: 'Tracking number no longer valid.' });
        } else if (err.status === 409) {
          setSubmit({ kind: 'error', message: 'Parcel is already delivered or in a terminal status.' });
        } else if (err.status === 402) {
          setSubmit({
            kind: 'error',
            message: 'Insufficient wallet balance for the ₦200 reschedule fee. Please top up.',
          });
        } else if (err.status === 400) {
          setSubmit({ kind: 'error', message: 'Selected date is invalid. Pick a future calendar day.' });
        } else {
          setSubmit({ kind: 'error', message: `Reschedule failed (${err.status}).` });
        }
      } else {
        setSubmit({ kind: 'error', message: 'Network error. Please try again.' });
      }
    }
  }

  return (
    <div>
      <PageHeader
        title="Reschedule Delivery"
        description="Pick a new delivery date for an in-flight parcel."
      />

      <div className="max-w-3xl mx-auto space-y-6">
        {/* ─── Step 1 — tracking-number lookup ───────────────────────────── */}
        <form
          onSubmit={(e) => { void handleLookup(e); }}
          className="bg-white rounded-xl border border-border p-6 space-y-4"
          noValidate
        >
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Find your parcel</h2>
          </div>
          <div>
            <label htmlFor="rs-tracking" className="block text-sm font-medium text-gray-700 mb-1">
              Tracking number <span className="text-danger">*</span>
            </label>
            <div className="flex gap-2">
              <input
                id="rs-tracking"
                type="text"
                value={tracking}
                onChange={(e) => setTracking(e.target.value.toUpperCase())}
                placeholder="e.g. EE123456785NG"
                className={`${inputClass} font-mono`}
              />
              <button
                type="submit"
                disabled={tracking.trim().length === 0 || lookup.kind === 'loading'}
                className={`px-5 py-2 rounded-lg font-semibold text-sm whitespace-nowrap ${
                  tracking.trim().length > 0 && lookup.kind !== 'loading'
                    ? 'bg-nipost-green text-white hover:bg-nipost-green-dark'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {lookup.kind === 'loading' ? (
                  <span className="flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking up…</span>
                ) : 'Look up'}
              </button>
            </div>
          </div>
          {lookup.kind === 'error' ? (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" role="alert">
              <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{lookup.message}</span>
            </div>
          ) : null}
          {lookup.kind === 'loaded' ? (
            <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 flex items-center justify-between">
              <span>
                Parcel <span className="font-mono font-semibold text-gray-900">{lookup.tracking}</span> —{' '}
                {lookup.existing.length === 0
                  ? 'No previous reschedules.'
                  : `${lookup.existing.length} previous reschedule${lookup.existing.length === 1 ? '' : 's'}.`}
              </span>
              <span className={`font-semibold ${isFirstReschedule ? 'text-nipost-green' : 'text-amber-700'}`}>
                Next fee: {formatNgn(nextFeeNgn)}
              </span>
            </div>
          ) : null}
        </form>

        {/* ─── Step 2 — pick a date ──────────────────────────────────────── */}
        {lookup.kind === 'loaded' ? (
          <div className="bg-white rounded-xl border border-border p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-nipost-green" />
              <h2 className="font-semibold text-gray-900">Pick a new delivery date</h2>
            </div>

            <div className="grid grid-cols-7 gap-2">
              {days.map((d) => {
                const isSelected = selectedDate === d.iso;
                return (
                  <button
                    key={d.iso}
                    type="button"
                    aria-label={`${d.label} ${d.day} ${d.month}`}
                    onClick={() => setSelectedDate(d.iso)}
                    disabled={d.disabled}
                    className={`flex flex-col items-center p-3 rounded-lg border ${
                      isSelected
                        ? 'border-nipost-green bg-nipost-green text-white'
                        : 'border-border hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <span className={`text-xs font-medium ${isSelected ? 'text-white/80' : 'text-muted'}`}>{d.label}</span>
                    <span className={`text-lg font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>{d.day}</span>
                    <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-muted'}`}>{d.month}</span>
                  </button>
                );
              })}
            </div>

            <div>
              <label htmlFor="rs-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <input
                id="rs-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
                placeholder="e.g. Travelling Monday, deliver Wednesday instead"
                className={inputClass}
              />
            </div>

            {/* Confirm panel */}
            <div className="rounded-lg border border-border bg-gray-50 p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">New delivery date</span>
                <span className="font-semibold text-gray-900">
                  {selectedDate ? new Date(`${selectedDate}T00:00:00Z`).toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fee</span>
                <span className={`font-semibold ${isFirstReschedule ? 'text-nipost-green' : 'text-amber-700'}`}>
                  {isFirstReschedule ? 'No charge (first reschedule)' : formatNgn(nextFeeNgn)}
                </span>
              </div>
              {wallet && wallet.balanceCached !== undefined ? (
                <div className="flex justify-between text-xs text-gray-500 pt-1 border-t border-border">
                  <span className="flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Wallet balance</span>
                  <span className="font-mono">{formatNgn(wallet.balanceCached)}</span>
                </div>
              ) : null}
            </div>

            {submit.kind === 'error' ? (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" role="alert">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{submit.message}</span>
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => { void handleConfirm(); }}
              disabled={!canConfirm}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors ${
                canConfirm
                  ? 'bg-nipost-green text-white hover:bg-nipost-green-dark'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {submit.kind === 'submitting' ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</span>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5" /> Confirm reschedule
                </>
              )}
            </button>
          </div>
        ) : null}

        {/* ─── Step 3 — success ───────────────────────────────────────────── */}
        {submit.kind === 'success' ? (
          <div
            className="bg-white rounded-xl border border-success/40 p-6 text-center space-y-3"
            role="status"
            data-testid="reschedule-success"
          >
            <CheckCircle2 className="w-10 h-10 text-nipost-green mx-auto" />
            <h2 className="text-lg font-bold text-gray-900">Reschedule confirmed</h2>
            <p className="text-sm text-gray-600">
              Your parcel will now be delivered on{' '}
              <span className="font-semibold">
                {new Date(`${submit.result.requestedDeliveryDate}T00:00:00Z`).toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>.
            </p>
            <div className="text-xs text-gray-500 flex justify-center gap-6">
              <span>Reference: <span className="font-mono text-gray-900" data-testid="reschedule-id">{submit.result.id}</span></span>
              <span>Fee charged: <span className="font-semibold text-gray-900">{formatNgn(submit.result.feeNgn)}</span></span>
            </div>
          </div>
        ) : null}

        {/* ─── Recent reschedules history ─────────────────────────────────── */}
        {lookup.kind === 'loaded' && lookup.existing.length > 0 ? (
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-muted" />
              <h2 className="font-semibold text-gray-900">Recent reschedules</h2>
            </div>
            <ul className="divide-y divide-border" data-testid="reschedule-history">
              {lookup.existing.map((r) => (
                <li key={r.id} className="py-2.5 text-sm flex items-center justify-between">
                  <div>
                    <p className="text-gray-900 font-medium">
                      {new Date(`${r.requestedDeliveryDate}T00:00:00Z`).toLocaleDateString('en-NG', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(r.createdAt).toLocaleString('en-NG')} · status{' '}
                      <span className="font-semibold">{r.status}</span>
                    </p>
                  </div>
                  <span className={`text-sm font-semibold ${r.feeMinor === 0 ? 'text-nipost-green' : 'text-amber-700'}`}>
                    {formatNgn(r.feeNgn)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
